import { Article } from '../articlesData';

export const articleUnderskrivPdfDokumentMedMitid: Article = {
  id: 'underskriv-pdf-dokument-med-mitid',
  slug: 'underskriv-pdf-dokument-med-mitid',
  title: 'Underskriv PDF og dokumenter med MitID: Komplet trin-for-trin guide (2026)',
  subtitle: 'Sådan underskriver du PDF-dokumenter med MitID – hurtigt, sikkert og juridisk bindende. Trin-for-trin vejledning til både private og virksomheder.',
  metaTitle: 'Underskriv PDF med MitID: Trin-for-trin Guide 2026 | KvikSign',
  metaDescription: 'Lær hvordan du underskriver PDF-dokumenter med MitID på under 1 minut. Gratis, sikkert og 100% juridisk bindende i EU. Se den komplette vejledning her.',
  canonicalUrl: 'https://kviksign.com/da/articles/underskriv-pdf-dokument-med-mitid',
  heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FPublic%20transport.webp?alt=media&token=ac951143-d83a-41f1-94a3-fd96012f650c',
  heroImageAlt: 'Digital underskrift og godkendelse af PDF-dokument med MitID på bærbar computer',
  category: 'Praktiske Guides',
  author: {
    name: 'KvikSign',
    role: 'Digital Signatur & Dokumentteknik',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-09-18',
  updatedAt: '2026-09-18',
  readingTime: '9 min læsetid',
  primaryKeyword: 'underskriv pdf med mitid',
  secondaryKeywords: [
    'digital underskrift pdf',
    'mitid underskrift af dokument',
    'hvordan underskriver man med mitid',
    'underskrive kontrakt mitid',
    'gratis underskriv pdf med mitid',
    'pades ltv pdf mitid signatur'
  ],
  keywords: [
    'underskriv pdf med mitid',
    'digital underskrift pdf',
    'mitid underskrift af dokument',
    'hvordan underskriver man med mitid',
    'underskrive kontrakt mitid',
    'gratis underskriv pdf med mitid',
    'pades ltv pdf mitid signatur',
    'mitid dokument signering'
  ],
  keyTakeaways: [
    'At underskrive en PDF med MitID tager under et minut og kræver ingen installation af software eller plugins.',
    'Underskriften er 100 % juridisk bindende i Danmark og hele EU efter eIDAS-forordningen for avancerede elektroniske signaturer.',
    'Når dokumentet er signeret, forsegles PDF\'en med et kryptografisk certifikat (PAdES-LTV), der forhindrer enhver form for efterfølgende ændring.',
    'Modtagere kan signere direkte fra deres mobiltelefon med MitID appen eller med kodeviser.',
    'KvikSign tilbyder 5 gratis månedlige signeringer til private og virksomheder, så du kan komme i gang uden kreditkort.'
  ],
  tableOfContents: [
    { id: 'hvorfor-underskrive-pdf-mitid', title: '1. Hvorfor underskrive PDF-dokumenter med MitID?' },
    { id: 'guide-til-modtagere', title: '2. Sådan underskriver du en PDF med MitID (guide til modtagere)' },
    { id: 'guide-til-afsendere', title: '3. Sådan sender du en PDF til underskrift med MitID (guide til virksomheder)' },
    { id: 'hvad-sker-der-teknisk', title: '4. Hvad sker der teknisk i PDF\'en ved en MitID-signering?' },
    { id: 'typiske-fejl-og-loesninger', title: '5. Typiske fejl og hvordan du undgår dem' },
    { id: 'faq-pdf-mitid', title: '6. Ofte stillede spørgsmål om PDF-signering med MitID' }
  ],
  sections: [
    {
      id: 'hvorfor-underskrive-pdf-mitid',
      title: '1. Hvorfor underskrive PDF-dokumenter med MitID?',
      paragraphs: [
        'Mange tror fejlagtigt, at det er nok at indsætte et billede af sin håndskrevne underskrift i et Word- eller PDF-dokument. Men juridisk set er et indsat billede af en signatur ekstremt sårbart for forfalskning og har meget ringe bevisværdi ved en domstol.',
        'Med MitID er situationen en helt anden: Underskriften er direkte knyttet til underskriverens officielle danske CPR-identitet via stærk to-faktor autentifikation. Dette skaber et uafviseligt bevis for, præcis hvem der har godkendt dokumentet og på hvilket nøjagtigt tidspunkt.',
        'Fordelene ved at signere PDF med MitID omfatter:'
      ],
      listItems: [
        'Ingen print eller scanning: Hele processen foregår 100 % digitalt på mobil, tablet eller PC.',
        'Højeste retsgyldighed: Opfylder kravene til Avanceret Elektronisk Signatur (AES) i henhold til EU-forordning nr. 910/2014 (eIDAS).',
        'Integritetssikring: Dokumentet bliver digitalt "plomberet". Hvis nogen forsøger at ændre et tal eller et ord i PDF\'en efter signering, bliver signaturen øjeblikkeligt markeret som ugyldig i Adobe Acrobat Reader.',
        'Fuld transparens: Både afsender og modtager modtager en kopi af det færdigsignerede dokument sammen med et detaljeret revisionscertifikat.'
      ]
    },
    {
      id: 'guide-til-modtagere',
      title: '2. Sådan underskriver du en PDF med MitID (guide til modtagere)',
      paragraphs: [
        'Hvis du har modtaget et link til at underskrive et dokument via KvikSign, er processen utrolig enkel og intuitiv:',
        'Trin 1 – Åbn linket: Klik på linket i den modtagne e-mail eller SMS. Du lander direkte i KvikSigns sikre underskriftsrum.',
        'Trin 2 – Gennemlæs dokumentet: Rul igennem PDF-aftalen og kontrollér, at alle vilkår stemmer. Eventuelle felter, der skal udfyldes (f.eks. adresse eller bemærkninger), er tydeligt markerede.',
        'Trin 3 – Klik på "Underskriv med MitID": Systemet åbner MitID-sikkerhedsvinduet, præcis som du kender det fra netbank og Borger.dk.',
        'Trin 4 – Godkend i MitID-appen: Åbn din MitID-app på telefonen, scan QR-koden eller indtast dit bruger-ID, og godkend anmodningen med et swipe.',
        'Trin 5 – Download dit eksemplar: Dokumentet er nu juridisk bindende underskrevet. Du kan downloade den forseglede PDF med det samme, og der sendes automatisk en kopi til din e-mail.'
      ]
    },
    {
      id: 'guide-til-afsendere',
      title: '3. Sådan sender du en PDF til underskrift med MitID (guide til virksomheder)',
      paragraphs: [
        'Hvis du som virksomhed eller privatperson ønsker at sende en kontrakt, lejekontrakt, fuldmagt eller gældsbrev til underskrift, tager det kun 30 sekunder på KvikSign:',
        '1. Opret gratis konto: Tilmeld dig med din e-mail eller log ind. Du modtager straks 5 gratis credits.',
        '2. Upload din PDF: Træk dit færdige PDF-dokument ind i afsender-modulet.',
        '3. Angiv modtager: Indtast modtagerens navn, e-mailadresse og evt. telefonnummer (hvis du ønsker SMS-notifikation eller 2-faktor lås).',
        '4. Vælg signeringsmetode: Vælg "MitID" (eller kombiner med Touch-signatur for udenlandske underskrivere). Du kan også aktivere CPR-validering for ekstra sikkerhed.',
        '5. Send dokumentet: KvikSign håndterer automatisk udsendelse, sporing af modtagelsen og automatiske påmindelser, hvis modtageren glemmer at signere.'
      ]
    },
    {
      id: 'hvad-sker-der-teknisk',
      title: '4. Hvad sker der teknisk i PDF\'en ved en MitID-signering?',
      paragraphs: [
        'Når en underskrift gennemføres med MitID via KvikSign, sker der en række avancerede kryptografiske beregninger bag kulisserne:',
        'For det første udregnes en matematisk hash-værdi (SHA-256) af det rå PDF-indhold. Denne hash-værdi fungerer som et unikt digitalt fingeraftryk af filen.',
        'Herefter knyttes MitID-verifikationsbeviset (indeholdende underskriverens bekræftede identitet og et officielt tidsstempel fra en betroet tidstjeneste) sammen med hash-værdien i en international PAdES-standard (PDF Advanced Electronic Signatures).',
        'Endelig tilføjes et LTV-stempel (Long-Term Validation). Det betyder, at signaturens gyldighed kan verificeres i Adobe Acrobat Reader i 10, 20 eller 30 år frem i tiden – selv hvis certifikatudstederen eller certifikaterne en dag skulle udløbe.'
      ]
    },
    {
      id: 'typiske-fejl-og-loesninger',
      title: '5. Typiske fejl og hvordan du undgår dem',
      paragraphs: [
        'Når man arbejder med digitale signaturer i PDF-format, kan man af og til støde på advarsler. Her er de vigtigste:',
        'Advarsel i Adobe: "At least one signature has problems" eller "Der er mindst én signatur med problemer": Dette skyldes næsten altid, at computerens Adobe Reader mangler Danmarks / EU\'s officielle rodcertifikater i sin lokale "Trust Store". Dokumentet er fuldt gyldigt, og fejlen forsvinder, når man opdaterer Adobes Adobe Approved Trust List (AATL).',
        'Fejl: "Dokumentet er ændret efter signering": Hvis nogen åbner den signerede PDF og forsøger at tilføje kommentarer, rotere sider eller fjerne et afsnit, brydes det digitale segl. Gem altid den originale, uændrede PDF som det juridiske bevis.'
      ]
    },
    {
      id: 'faq-pdf-mitid',
      title: '6. Ofte stillede spørgsmål om PDF-signering med MitID',
      paragraphs: [
        'Her er svar på de mest stillede spørgsmål om underskrift af PDF-dokumenter med MitID.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Koster det noget for modtageren at underskrive en PDF med MitID?',
      answer: 'Nej. Det er altid 100 % gratis for den, der modtager og underskriver et dokument. Afsenderen betaler en lille credit, og KvikSign giver 5 gratis credits ved oprettelse.'
    },
    {
      question: 'Kan jeg underskrive en PDF med MitID på iPhone eller Android?',
      answer: 'Ja, absolut. KvikSign er fuldt optimeret til smartphones. Når du klikker på underskriv, åbnes MitID-appen automatisk, eller du kan godkende med et enkelt swipe.'
    },
    {
      question: 'Hvor mange personer kan underskrive den samme PDF?',
      answer: 'Der er ingen begrænsning. KvikSign understøtter både parallel signering (hvor alle får dokumentet samtidigt) og sekventiel signering (hvor dokumentet sendes videre i en bestemt rækkefølge).'
    },
    {
      question: 'Er en MitID-signeret PDF gyldig hos banker og tinglysning?',
      answer: 'Ja, banker, advokater, revisorer og offentlige myndigheder i Danmark accepterer digitale PDF-signaturer med MitID som fuldt bindende dokumentation.'
    }
  ],
  sources: [
    {
      title: 'Adobe – PAdES Digital Signatures in PDF',
      url: 'https://helpx.adobe.com/acrobat/using/digital-ids.html'
    },
    {
      title: 'Digitaliseringsstyrelsen – Fakta om MitID',
      url: 'https://www.mitid.dk/'
    },
    {
      title: 'EU eIDAS Standarder (ETSI EN 319 142)',
      url: 'https://www.etsi.org/'
    }
  ],
  disclaimer: 'Denne guide er til teknisk og praktisk vejledning om digital PDF-signering med MitID og erstatter ikke juridisk rådgivning i tvister eller retssager.'
};
